import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuthStore from '../store/authStore';
import PublicSiteHeader from '../components/PublicSiteHeader';
import { authService, userService, teacherProfileService, academicService, resourceService } from '../services/api';
import { Card, Badge, Button, EmptyState, FullPageSpinner, Modal, Field, Input, Select, Textarea } from '../components/ui';
import { StarDisplay } from '../components/StarRating';
import { formatPrice } from '../utils/currency';
import { useNotification } from '../contexts/NotificationContext';
import SocialLinks, { cleanSocialLinks, SocialLinksEditor } from '../components/SocialLinks';
import PlatformFooter from '../components/PlatformFooter';
import { getApiErrorMessage } from '../utils/apiErrors';

const MAX_COVER_SIZE = 5 * 1024 * 1024;
const MAX_SIGNATURE_SIZE = 2 * 1024 * 1024;
const RESOURCE_CATEGORIES = ['policy', 'form', 'material', 'other'];
const EMPTY_RESOURCE_FORM = { title: '', description: '', category: 'other', sourceType: 'link', external_url: '', file: null, is_public: true };

function TeacherProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: currentUser, updateUser } = useAuthStore();
  const { addNotification } = useNotification();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [pendingOwnProfile, setPendingOwnProfile] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [activeTab, setActiveTab] = useState('courses');

  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [resources, setResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', bio: '', qualifications: '', specializations: '', social_links: {} });
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const coverPreviewIsObjectUrl = useRef(false);
  const avatarPreviewIsObjectUrl = useRef(false);
  const signaturePreviewIsObjectUrl = useRef(false);

  const [resourceModalOpen, setResourceModalOpen] = useState(false);
  const [resourceForm, setResourceForm] = useState(EMPTY_RESOURCE_FORM);
  const [resourceSaving, setResourceSaving] = useState(false);

  const isOwnProfile = currentUser && String(currentUser.id) === String(id);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    setPendingOwnProfile(false);
    try {
      const res = await userService.getPublicProfile(id);
      setProfileData(res.data);
    } catch (err) {
      if (isOwnProfile && err.response && err.response.status === 404) {
        try {
          const mine = await teacherProfileService.getMine();
          setProfileData({
            user: currentUser,
            profile: mine.data,
            stats: { courses_count: 0, students_count: 0, average_rating: null },
          });
          setPendingOwnProfile(true);
        } catch (innerErr) {
          setNotFound(true);
        }
      } else {
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (!profileData || activeTab !== 'courses') return;
    setCoursesLoading(true);
    academicService.getModules({ teacher: id, page_size: 100 })
      .then((res) => {
        const data = res.data;
        setCourses(Array.isArray(data) ? data : data.results || []);
      })
      .catch(() => setCourses([]))
      .finally(() => setCoursesLoading(false));
  }, [profileData, activeTab, id]);

  const fetchResources = useCallback(() => {
    setResourcesLoading(true);
    resourceService.getResources({ teacher: id })
      .then((res) => {
        const data = res.data;
        setResources(Array.isArray(data) ? data : data.results || []);
      })
      .catch(() => setResources([]))
      .finally(() => setResourcesLoading(false));
  }, [id]);

  useEffect(() => {
    if (!profileData || activeTab !== 'resources') return;
    fetchResources();
  }, [profileData, activeTab, fetchResources]);

  useEffect(() => {
    return () => {
      if (coverPreviewIsObjectUrl.current && coverPreview) URL.revokeObjectURL(coverPreview);
      if (avatarPreviewIsObjectUrl.current && avatarPreview) URL.revokeObjectURL(avatarPreview);
      if (signaturePreviewIsObjectUrl.current && signaturePreview) URL.revokeObjectURL(signaturePreview);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = () => {
    const profile = profileData.profile;
    setEditForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      bio: profile.bio || '',
      qualifications: profile.qualifications || '',
      specializations: (profile.specializations || []).join(', '),
      social_links: profile.social_links || {},
    });
    setCoverFile(null);
    setCoverPreview(profile.cover_photo || null);
    coverPreviewIsObjectUrl.current = false;
    setAvatarFile(null);
    setAvatarPreview(user.profile_picture || null);
    avatarPreviewIsObjectUrl.current = false;
    setSignatureFile(null);
    setSignaturePreview(profile.signature_url || null);
    signaturePreviewIsObjectUrl.current = false;
    setSaveError('');
    setEditing(true);
  };

  const handleCoverChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setSaveError(t('Please select an image file'));
      return;
    }
    if (f.size > MAX_COVER_SIZE) {
      setSaveError(t('Image must be smaller than 5MB'));
      return;
    }
    setSaveError('');
    setCoverFile(f);
    if (coverPreviewIsObjectUrl.current && coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverPreview(URL.createObjectURL(f));
    coverPreviewIsObjectUrl.current = true;
  };

  const handleAvatarChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setSaveError(t('Please select an image file'));
      return;
    }
    if (f.size > MAX_COVER_SIZE) {
      setSaveError(t('Image must be smaller than 5MB'));
      return;
    }
    setSaveError('');
    setAvatarFile(f);
    if (avatarPreviewIsObjectUrl.current && avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarPreview(URL.createObjectURL(f));
    avatarPreviewIsObjectUrl.current = true;
  };

  const handleSignatureChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setSaveError(t('Please select an image file'));
      return;
    }
    if (file.size > MAX_SIGNATURE_SIZE) {
      setSaveError(t('Signature image must be smaller than 2MB'));
      return;
    }
    setSaveError('');
    setSignatureFile(file);
    if (signaturePreviewIsObjectUrl.current && signaturePreview) URL.revokeObjectURL(signaturePreview);
    setSignaturePreview(URL.createObjectURL(file));
    signaturePreviewIsObjectUrl.current = true;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editForm.first_name.trim() || !editForm.last_name.trim()) {
      setSaveError(t('First and last name are required'));
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        bio: editForm.bio,
        qualifications: editForm.qualifications,
        specializations: editForm.specializations.split(',').map((s) => s.trim()).filter(Boolean),
        social_links: cleanSocialLinks(editForm.social_links),
      };
      if (coverFile) payload.cover_photo = coverFile;
      if (signatureFile) payload.signature = signatureFile;
      const nameForm = new FormData();
      nameForm.append('first_name', editForm.first_name);
      nameForm.append('last_name', editForm.last_name);
      if (avatarFile) nameForm.append('profile_picture', avatarFile);
      const [profileRes, userRes] = await Promise.all([
        teacherProfileService.updateMine(payload),
        authService.updateProfile(nameForm),
      ]);
      const updatedUser = userRes.data || userRes;
      updateUser(updatedUser);
      setProfileData((prev) => ({
        ...prev,
        user: { ...prev.user, first_name: updatedUser.first_name, last_name: updatedUser.last_name, profile_picture: updatedUser.profile_picture },
        profile: profileRes.data,
      }));
      setEditing(false);
      setCoverFile(null);
      setAvatarFile(null);
      setSignatureFile(null);
    } catch (err) {
      const data = err.response && err.response.data;
      const message = data && typeof data === 'object'
        ? (data.detail || Object.values(data).flat().join(' '))
        : null;
      setSaveError(message || t('Something went wrong. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const openResourceModal = () => {
    setResourceForm(EMPTY_RESOURCE_FORM);
    setResourceModalOpen(true);
  };

  const saveResource = async () => {
    if (!resourceForm.title.trim()) {
      addNotification(t('Title is required'), 'error');
      return;
    }
    if (resourceForm.sourceType === 'link' && !resourceForm.external_url.trim()) {
      addNotification(t('Provide a link or switch to file upload'), 'error');
      return;
    }
    if (resourceForm.sourceType === 'file' && !resourceForm.file) {
      addNotification(t('Choose a file to upload'), 'error');
      return;
    }
    setResourceSaving(true);
    try {
      await resourceService.createResource({
        title: resourceForm.title,
        description: resourceForm.description,
        category: resourceForm.category,
        is_public: resourceForm.is_public,
        external_url: resourceForm.sourceType === 'link' ? resourceForm.external_url : '',
        file: resourceForm.sourceType === 'file' ? resourceForm.file : null,
      });
      addNotification(t('Resource added'), 'success');
      setResourceModalOpen(false);
      fetchResources();
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, 'Failed to save resource'), 'error');
    } finally {
      setResourceSaving(false);
    }
  };

  if (loading) return <FullPageSpinner />;

  const publicHeader = <PublicSiteHeader active="teachers" />;

  if (notFound) {
    const notFoundContent = (
      <Card>
        <EmptyState
          title={t('Teacher not found')}
          description={t('This teacher does not have a public profile yet.')}
          action={<Button variant="secondary" onClick={() => navigate('/store')}>{t('Back to Store')}</Button>}
        />
      </Card>
    );

    return (
      <div className="h-screen overflow-y-auto bg-surface-50 text-gray-900">
        {publicHeader}
        <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">{notFoundContent}</main>
        <PlatformFooter />
      </div>
    );
  }

  const { user, profile, stats } = profileData;
  const fullName = `${user.first_name} ${user.last_name}`.trim();
  const initials = `${(user.first_name || '?')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase();

  const profileContent = (
    <>
      {pendingOwnProfile && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              {t('Your teacher account is pending approval — this profile is only visible to you until an administrator approves it.')}
            </div>
          )}

          {/* Cover + avatar */}
          <div className="relative mb-16">
            <div
              className={`h-40 sm:h-56 rounded-xl ${profile.cover_photo ? 'bg-cover bg-center' : 'bg-gradient-to-r from-primary-600 to-purple-600'}`}
              style={profile.cover_photo ? { backgroundImage: `url(${profile.cover_photo})` } : {}}
            />
            <div className="absolute left-6 sm:left-10 -bottom-12 w-24 h-24 sm:w-32 sm:h-32 rounded-full ring-4 ring-gray-50 overflow-hidden bg-gray-200 flex items-center justify-center shadow-md">
              {user.profile_picture ? (
                <img src={user.profile_picture} alt={fullName} className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl sm:text-4xl font-display font-bold text-gray-500">{initials}</span>
              )}
            </div>
            {isOwnProfile && (
              <div className="absolute right-4 -bottom-12">
                <Button variant="secondary" size="sm" onClick={openEdit}>{t('Edit Profile')}</Button>
              </div>
            )}
          </div>

          {/* Identity + stats */}
          <Card className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl sm:text-2xl font-display font-bold text-gray-900">{fullName}</h1>
                  <Badge tone="blue">{t('Teacher')}</Badge>
                </div>
                {profile.bio && <p className="text-gray-600 mt-2 whitespace-pre-line">{profile.bio}</p>}
                {profile.qualifications && (
                  <p className="text-sm text-gray-500 mt-2">
                    <span className="font-medium text-gray-700">{t('Qualifications')}: </span>
                    {profile.qualifications}
                  </p>
                )}
                {profile.specializations && profile.specializations.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {profile.specializations.map((s, i) => (
                      <Badge key={i} tone="purple">{s}</Badge>
                    ))}
                  </div>
                )}
                <SocialLinks links={profile.social_links} className="mt-4" />
              </div>
              <div className="flex sm:flex-col gap-4 sm:gap-2 shrink-0 sm:border-l sm:pl-6 sm:border-gray-100">
                <div className="text-center sm:text-right">
                  <p className="text-lg font-bold text-gray-900">{stats.courses_count}</p>
                  <p className="text-xs text-gray-500">{t('Courses')}</p>
                </div>
                <div className="text-center sm:text-right">
                  <p className="text-lg font-bold text-gray-900">{stats.students_count}</p>
                  <p className="text-xs text-gray-500">{t('Students')}</p>
                </div>
                <div className="text-center sm:text-right">
                  {stats.average_rating != null ? (
                    <div className="flex items-center justify-center sm:justify-end gap-1">
                      <StarDisplay value={stats.average_rating} size="w-3.5 h-3.5" />
                      <span className="text-sm font-bold text-gray-900">{stats.average_rating}</span>
                    </div>
                  ) : (
                    <p className="text-lg font-bold text-gray-400">—</p>
                  )}
                  <p className="text-xs text-gray-500">{t('Rating')}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-md mb-6">
            <div className="flex border-b">
              <button
                onClick={() => setActiveTab('courses')}
                className={`px-6 py-3 font-medium transition whitespace-nowrap ${
                  activeTab === 'courses' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-600 hover:text-primary-600'
                }`}
              >
                {t('Courses')}
              </button>
              <button
                onClick={() => setActiveTab('resources')}
                className={`px-6 py-3 font-medium transition whitespace-nowrap ${
                  activeTab === 'resources' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-600 hover:text-primary-600'
                }`}
              >
                {t('Resources')}
              </button>
              {isOwnProfile && activeTab === 'resources' && (
                <div className="flex-1 flex items-center justify-end pe-4">
                  <Button size="sm" onClick={openResourceModal}>+ {t('Add Resource')}</Button>
                </div>
              )}
            </div>

            <div className="p-5">
              {activeTab === 'courses' ? (
                coursesLoading ? (
                  <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
                ) : courses.length === 0 ? (
                  <EmptyState title={t('No courses yet')} />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {courses.map((m) => (
                      <Card key={m.id} padded={false} className="flex flex-col overflow-hidden hover:shadow-card-hover transition">
                        <div
                          className="h-32 bg-gray-100 bg-center bg-cover"
                          style={m.thumbnail ? { backgroundImage: `url(${m.thumbnail})` } : {}}
                        >
                          {!m.thumbnail && (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700">
                              <svg className="w-8 h-8 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="p-4 flex-1 flex flex-col">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <Badge tone={m.price && Number(m.price) > 0 ? 'blue' : 'green'}>
                              {m.price && Number(m.price) > 0 ? formatPrice(m.price, m.currency) : t('Free')}
                            </Badge>
                            {m.average_rating != null && (
                              <div className="flex items-center gap-1">
                                <StarDisplay value={m.average_rating} size="w-3 h-3" />
                                <span className="text-xs text-gray-500">{Number(m.average_rating).toFixed(1)}</span>
                              </div>
                            )}
                          </div>
                          <h3 className="font-display font-bold text-gray-900 mb-1.5">{m.name}</h3>
                          <p className="text-sm text-gray-600 line-clamp-2 flex-1">{m.description}</p>
                          <Link to={`/modules/${m.id}`} className="mt-3 text-sm font-medium text-primary-600 hover:text-primary-700">
                            {t('View')}
                          </Link>
                        </div>
                      </Card>
                    ))}
                  </div>
                )
              ) : resourcesLoading ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
              ) : resources.length === 0 ? (
                <EmptyState title={t('No resources shared yet')} />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {resources.map((r) => (
                    <Card key={r.id} className="flex flex-col">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <Badge tone="blue">{t(r.category)}</Badge>
                      </div>
                      <h3 className="font-display font-bold text-gray-900 mb-1">{r.title}</h3>
                      {r.description && <p className="text-sm text-gray-600 flex-1 line-clamp-3">{r.description}</p>}
                      <a
                        href={r.file || r.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-700"
                      >
                        {r.file ? t('Download') : t('Open Link')}
                      </a>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
    </>
  );

  const modals = (
    <>
      <Modal open={editing} onClose={() => setEditing(false)} title={t('Edit Profile')} size="lg">
        <form onSubmit={handleSave} className="space-y-4">
          {saveError && <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{saveError}</div>}

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('First Name')} required>
              <Input value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} />
            </Field>
            <Field label={t('Last Name')} required>
              <Input value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} />
            </Field>
          </div>

          <Field label={t('Cover Photo')}>
            <div className="h-28 rounded-lg bg-gray-100 bg-center bg-cover mb-2" style={coverPreview ? { backgroundImage: `url(${coverPreview})` } : {}} />
            <input type="file" accept="image/*" onChange={handleCoverChange} />
          </Field>

          <Field label={t('Avatar')}>
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center shrink-0">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="avatar preview" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-lg font-display font-bold text-gray-500">
                    {`${(editForm.first_name || '?')[0] || ''}${(editForm.last_name || '')[0] || ''}`.toUpperCase()}
                  </span>
                )}
              </div>
              <input type="file" accept="image/*" onChange={handleAvatarChange} />
            </div>
          </Field>

          <Field
            label={t('Certificate Signature')}
            hint={t('Upload a clear PNG with a transparent or white background. It will appear on certificates you approve.')}
          >
            <div className="rounded-xl border border-dashed border-gray-300 bg-white p-3">
              <div className="mb-3 flex h-20 items-center justify-center rounded-lg bg-gray-50">
                {signaturePreview ? (
                  <img src={signaturePreview} alt={t('Signature preview')} className="max-h-16 max-w-full object-contain" />
                ) : (
                  <span className="text-sm text-gray-400">{t('No signature uploaded')}</span>
                )}
              </div>
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleSignatureChange} className="w-full text-sm" />
            </div>
          </Field>

          <Field label={t('Bio')}>
            <Textarea
              rows={3}
              value={editForm.bio}
              onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
            />
          </Field>

          <Field label={t('Qualifications')}>
            <Textarea
              rows={2}
              value={editForm.qualifications}
              onChange={(e) => setEditForm({ ...editForm, qualifications: e.target.value })}
            />
          </Field>

          <Field label={t('Specializations (comma separated)')}>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={editForm.specializations}
              onChange={(e) => setEditForm({ ...editForm, specializations: e.target.value })}
              placeholder={t('e.g. Mathematics, Physics, Chemistry')}
            />
          </Field>

          <div className="border-t border-surface-200 pt-4">
            <h3 className="font-display font-bold text-gray-900">{t('Social media links')}</h3>
            <p className="mb-4 mt-1 text-sm text-gray-500">{t('Add the profiles you want visitors to see on your public teacher page.')}</p>
            <SocialLinksEditor value={editForm.social_links} onChange={(social_links) => setEditForm({ ...editForm, social_links })} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setEditing(false)}>{t('Cancel')}</Button>
            <Button type="submit" loading={saving}>{t('Save')}</Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={resourceModalOpen}
        onClose={() => setResourceModalOpen(false)}
        title={t('Add Resource')}
        footer={<>
          <Button variant="secondary" onClick={() => setResourceModalOpen(false)}>{t('Cancel')}</Button>
          <Button loading={resourceSaving} onClick={saveResource}>{t('Save')}</Button>
        </>}
      >
        <div className="space-y-4">
          <Field label={t('Title')} required>
            <Input value={resourceForm.title} onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })} autoFocus />
          </Field>
          <Field label={t('Description')}>
            <Textarea rows={2} value={resourceForm.description} onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })} />
          </Field>
          <Field label={t('Category')}>
            <Select value={resourceForm.category} onChange={(e) => setResourceForm({ ...resourceForm, category: e.target.value })}>
              {RESOURCE_CATEGORIES.map((c) => <option key={c} value={c}>{t(c)}</option>)}
            </Select>
          </Field>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setResourceForm({ ...resourceForm, sourceType: 'link' })}
              className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium border ${resourceForm.sourceType === 'link' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-gray-200 text-gray-500'}`}
            >
              {t('Link')}
            </button>
            <button
              type="button"
              onClick={() => setResourceForm({ ...resourceForm, sourceType: 'file' })}
              className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium border ${resourceForm.sourceType === 'file' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-gray-200 text-gray-500'}`}
            >
              {t('Upload File')}
            </button>
          </div>

          {resourceForm.sourceType === 'link' ? (
            <Field label={t('URL')} required>
              <Input value={resourceForm.external_url} onChange={(e) => setResourceForm({ ...resourceForm, external_url: e.target.value })} placeholder="https://..." />
            </Field>
          ) : (
            <Field label={t('File')} required>
              <input
                type="file"
                onChange={(e) => setResourceForm({ ...resourceForm, file: e.target.files?.[0] || null })}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
              />
            </Field>
          )}

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={resourceForm.is_public} onChange={(e) => setResourceForm({ ...resourceForm, is_public: e.target.checked })} />
            {t('Visible to everyone (uncheck to keep private to you and admins)')}
          </label>
        </div>
      </Modal>
    </>
  );

  return (
    <div className="h-screen overflow-y-auto bg-surface-50 text-gray-900">
      {publicHeader}
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">{profileContent}</main>
      <PlatformFooter />
      {modals}
    </div>
  );
}

export default TeacherProfile;
